// Документ «расчёт сотрудника»: заголовок, итог, пробелы, таблица блоков и подтаблицы.
//
// Порядок сверху вниз повторяет вопросы финансового директора. Кого и за что считаем (заголовок) →
// можно ли верить числам (плашка «итог предварительный») → сколько всего и сколько осталось
// выплатить (итог) → чего не хватает и куда идти (пробелы, предупреждения) → откуда цифры (таблица
// по месяцам и объектам, как в листе заказчика) → из чего сложились авансы, форма, билеты.
//
// Числа и слова пришли с сервера готовыми: он посчитал деньги, назвал пробелы и предупреждения.
// Кабинет ничего не считает и не округляет, а только раскладывает по экрану.
//
// ## Excel и бумага
//
// У документа две кнопки, как у расчётного листа за месяц: «Выгрузить в Excel» и «Напечатать».
// Файл собирает сервер из того же документа, в раскладке листа заказчика «Расчёт Шаврин», и
// границы месяцев передаются те, что показаны: не заданные, они выбрались бы заново, и файл мог бы
// разойтись с экраном. На бумагу идёт только документ: полоса разделов, выбор человека и сами
// кнопки в печать не попадают (`data-печать`), а в блоке пробелов остаётся текст без кнопок.

import type { PayslipPeriodOut, PayslipPeriodParams } from '@/shared/api/endpoints/payslip';
import { exportPayslipPeriod } from '@/shared/api/endpoints/payslip';
import { ExportButton } from '@/shared/reports/ExportButton';
import { PrintButton } from '@/shared/reports/PrintButton';
import { Block } from '@/shared/ui/block';

import { БлокиРасчёта } from './БлокиРасчёта';
import { ИтогЮрлица } from './Итоги';
import { ПлашкаПредварительно, Предупреждения, Пробелы } from './Плашки';
import { Подтаблицы } from './Подтаблицы';
import { заголовокРасчёта, итогНеизвестен, непосчитанноеВБлоках, фразаПериода } from './тексты';

/** Границы, которые показал документ: ровно их и просит файл. */
function границыДокумента(данные: PayslipPeriodOut): PayslipPeriodParams {
  const период = данные.period;
  if (период === null || период === undefined) {
    return {};
  }
  return {
    year_from: период.year_from,
    month_from: период.month_from,
    year_to: период.year_to,
    month_to: период.month_to,
  };
}

export function Документ({ данные }: { данные: PayslipPeriodOut }) {
  const фраза =
    данные.period === null || данные.period === undefined ? null : фразаПериода(данные.period);
  const юрлиц = данные.totals.length;
  const блокиЮрлица = (юрлицо: string) =>
    данные.blocks.filter((блок) => блок.legal_entity_id === юрлицо);
  // Итог неизвестен у всех юрлиц: плашка в блоке итога («Итог пока не посчитан») говорит об этом
  // точнее, а общая «Итог предварительный» над ней добавляла вторую жёлтую плашку с теми же
  // словами и причинами, которые итоговый блок и так называет (находка прохода по боевым данным
  // 01.10.2026: две плашки подряд над одним и тем же прочерком).
  const всеИтогиНеизвестны =
    юрлиц > 0 &&
    данные.totals.every((итог) =>
      итогНеизвестен(непосчитанноеВБлоках(блокиЮрлица(итог.legal_entity_id)), итог),
    );
  return (
    // Документ помечен: печать разворачивает его целиком, а не печатает видимый кусок.
    <div data-печать='документ' className='flex flex-col gap-4 p-5 узко:p-4'>
      <div className='flex flex-wrap items-start justify-between gap-x-4 gap-y-2'>
        <h2 className='min-w-0 flex-1 basis-72 text-label-lg text-text-strong-950'>
          {заголовокРасчёта(данные)}
        </h2>
        {/* Оба действия одной группой у правого края: врозь они читались бы как разные вещи. */}
        <div data-печать='скрыть' className='flex shrink-0 items-start gap-2'>
          <ExportButton
            // Свой расчёт человек вправе сохранить и показать, а чужой без права на ведомости
            // сервер не отдаёт вовсе: права на отчёты по компании здесь не спрашиваются.
            требуетПраво={false}
            собрать={() => exportPayslipPeriod(данные.person.employee_id, границыДокумента(данные))}
          />
          <PrintButton />
        </div>
      </div>

      <ПлашкаПредварительно причины={всеИтогиНеизвестны ? [] : данные.preliminary_reasons} />

      {юрлиц === 0 && данные.empty_reason !== null && данные.empty_reason !== undefined && (
        // Денег нет, но документ не пуст: пробелы ниже называют, чего не хватает. Причину
        // пишем один раз, на месте итога, которого нет.
        <p className='text-paragraph-sm text-text-sub-600'>{данные.empty_reason}.</p>
      )}

      {данные.totals.map((итог) => (
        <ИтогЮрлица
          key={итог.legal_entity_id}
          итог={итог}
          заголовок={юрлиц > 1 ? итог.legal_entity_name : 'Итого за всё время'}
          фраза={фраза}
          блоки={блокиЮрлица(итог.legal_entity_id)}
          предварительно={данные.is_preliminary}
          человек={данные.person.employee_id}
        />
      ))}

      <Пробелы пробелы={данные.gaps} employeeId={данные.person.employee_id} />
      <Предупреждения предупреждения={данные.warnings} />

      {данные.totals.map((итог) => (
        <Block
          key={итог.legal_entity_id}
          title={юрлиц > 1 ? `Месяцы и объекты: ${итог.legal_entity_name}` : 'Месяцы и объекты'}
          note='от старых к новым, как в листе расчёта'
          flush
        >
          <БлокиРасчёта
            подпись={`Расчёт по месяцам и объектам, ${итог.legal_entity_name}`}
            итог={итог}
            блоки={блокиЮрлица(итог.legal_entity_id)}
            пробелы={данные.gaps}
          />
        </Block>
      ))}

      <Подтаблицы данные={данные} />

      {данные.notes.length > 0 && (
        <section className='flex flex-col gap-1'>
          <h3 className='text-label-sm text-text-sub-600'>Как читать расчёт</h3>
          <ul className='flex flex-col gap-0.5'>
            {данные.notes.map((заметка) => (
              <li key={заметка} className='text-paragraph-xs text-text-sub-600'>
                {заметка}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
